import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, ShoppingCart, UserPlus, Smartphone, Handshake } from 'lucide-react';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

export default function HowItWorks() {
  const { t } = useTranslation();

  const steps = [
    { 
      num: '01', 
      icon: ShoppingCart,
      image: '/assets/step-plan.png',
      title: t('howItWorks.step1.title'), 
      desc: t('howItWorks.step1.description'),
      details: [
        t('howItWorks.step1.d1'),
        t('howItWorks.step1.d2'),
        t('howItWorks.step1.d3'),
        t('howItWorks.step1.d4'),
      ]
    },
    { 
      num: '02', 
      icon: UserPlus,
      image: '/assets/step-profile.png',
      title: t('howItWorks.step2.title'), 
      desc: t('howItWorks.step2.description'),
      details: [
        t('howItWorks.step2.d1'),
        t('howItWorks.step2.d2'),
        t('howItWorks.step2.d3'),
        t('howItWorks.step2.d4'),
      ]
    },
    { 
      num: '03', 
      icon: Smartphone,
      image: '/assets/step-network.png',
      title: t('howItWorks.step3.title'), 
      desc: t('howItWorks.step3.description'),
      details: [
        t('howItWorks.step3.d1'),
        t('howItWorks.step3.d2'),
        t('howItWorks.step3.d3'),
        t('howItWorks.step3.d4'),
      ]
    },
  ];

  return (
    <div className="min-h-screen pt-24 pb-16 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div {...fadeUp} className="text-center mb-16">
          <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-4">
            {t('howItWorks.title')}
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {t('howItWorks.subtitle')}
          </p>
        </motion.div>

        {/* Steps */}
        <div className="space-y-12 mb-16">
          {steps.map((step, idx) => (
            <motion.div
              key={idx}
              {...fadeUp}
              transition={{ delay: idx * 0.15 }}
              className={`flex flex-col ${idx % 2 === 1 ? 'lg:flex-row-reverse' : 'lg:flex-row'} gap-8 items-center`}
              data-testid={`step-${idx}`}
            >
              {/* Visual — Photo */}
              <div className="lg:w-1/2">
                <div className="relative rounded-3xl overflow-hidden shadow-xl">
                  <img
                    src={step.image}
                    alt={step.title}
                    className="w-full h-72 lg:h-80 object-cover"
                    loading="lazy"
                  />
                  <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-xl px-4 py-2">
                    <span className="font-heading text-3xl font-bold text-slate-900">{step.num}</span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="lg:w-1/2">
                <div className="bg-white rounded-3xl p-8 shadow-soft">
                  <h3 className="font-heading text-2xl font-semibold text-slate-900 mb-4">
                    {step.title}
                  </h3>
                  <p className="text-slate-600 mb-6">{step.desc}</p>
                  <ul className="space-y-3">
                    {step.details.map((detail, i) => (
                      <li key={i} className="flex items-center gap-3 text-sm text-slate-600">
                        <div className="w-6 h-6 bg-[#0A2540]/10 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-[#0A2540] font-medium text-xs">{i + 1}</span>
                        </div>
                        {detail}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Contactless Card Section */}
        <motion.div {...fadeUp} className="bg-white rounded-3xl shadow-soft mb-16 overflow-hidden">
          <div className="flex flex-col lg:flex-row items-center gap-0">
            {/* Video */}
            <div className="lg:w-1/2 w-full">
              <video
                className="w-full h-72 lg:h-96 object-cover"
                controls
                preload="metadata"
                poster=""
                data-testid="card-demo-video"
              >
                <source src="/assets/card-demo.mp4" type="video/mp4" />
              </video>
            </div>
            {/* Content */}
            <div className="lg:w-1/2 p-8 lg:p-12">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center">
                  <Handshake className="w-6 h-6 text-white" />
                </div>
                <h2 className="font-heading text-2xl lg:text-3xl font-bold text-slate-900">
                  {t('howItWorks.contactless.title')}
                </h2>
              </div>
              <p className="text-slate-600 leading-relaxed mb-4">
                {t('howItWorks.contactless.p1')}
              </p>
              <p className="text-slate-600 leading-relaxed">
                {t('howItWorks.contactless.p2')}
              </p>
            </div>
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div {...fadeUp} className="text-center">
          <Link to="/products">
            <Button className="btn-primary text-lg px-10 py-6 h-auto" data-testid="get-started-btn">
              {t('howItWorks.cta')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </div>
  );
}
